import { displayAttribute } from "../i18n/LocalizedCopy";
import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
import { useI18n } from "../i18n/LanguageContext";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import React from "react";
import { imageForFeature } from "./rubricImages";
import "./RubricImages.css";
import { Link } from "react-router-dom";

import EternuaIcon, { iconForPath } from "../components/EternuaIcon";

import { CONTENT_NATURE, FUNCTIONAL_STATUS } from "./registry";
import "./FeatureStatus.css";

export function FunctionalStatusBadge({ feature, withPrefix = false }) {
  useSourceLanguage();
  const { translate, translateLabel } = useNavigationTranslations();
  if (!feature) return null;
  const label = FUNCTIONAL_STATUS[feature.functionalStatus]?.label || feature.functionalStatusLabel || "Statut inconnu";
  const translatedLabel = translate(`navigation.status.${feature.functionalStatus}`, translateLabel(label));
  const statusClass = String(feature.functionalStatus || "DISABLED").toLowerCase();
  return <span className={`feature-badge feature-badge--${statusClass}`}>{withPrefix ? `${translateLabel("Statut")} · ${translatedLabel}` : translatedLabel}</span>;
}

export function ContentNatureBadge({ feature }) {
  useSourceLanguage();
  const { translate, translateLabel } = useNavigationTranslations();
  const label = CONTENT_NATURE[feature?.contentNature] || feature?.contentNatureLabel;
  if (!feature?.contentNature || !label) return null;
  return <span className={`feature-badge feature-badge--nature feature-badge--${feature.contentNature}`}>{translate(`navigation.nature.${feature.contentNature}`, translateLabel(label))}</span>;
}

export function FeatureBadges({ feature, documentProspective = false }) {
  useSourceLanguage();
  const { translateLabel } = useNavigationTranslations();
  return (
    <div className="feature-badges" aria-label={translateLabel("Statut et nature du contenu")}>
      <FunctionalStatusBadge feature={feature} />
      <ContentNatureBadge feature={feature} />
      {documentProspective ? <span className="feature-badge feature-badge--document">{translateLabel("Document prospectif")}</span> : null}
    </div>
  );
}

export function FeaturePrimaryAction({ "data-et-copy": __etCopy = "", feature, className = "feature-action", label, temple = false }) {
  useSourceLanguage();
  const { translateLabel } = useNavigationTranslations();
  if (!feature?.route) return null;
  const meta = FUNCTIONAL_STATUS[feature.functionalStatus] || FUNCTIONAL_STATUS.COMING_SOON;
  if (["DISABLED", "RETIRED"].includes(feature.functionalStatus)) return null;
  if (!temple) {
    return <Link className={className} to={feature.route}>{translateLabel(label || meta.actionLabel)}<span aria-hidden="true"> →</span></Link>;
  }
  return (
    <Link className={className} to={feature.route}>
      <span>{translateLabel(label || meta.actionLabel)}</span>
      <span className="feature-action__arrow" aria-hidden="true">→</span>
    </Link>
  );
}

function TempleFeatureIcon({ featureKey, route }) {
  useSourceLanguage();
  const commonProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.45",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (featureKey) {
    case "today-buy":
      return <svg {...commonProps}><path d="M12 3.3 20.4 12 12 20.7 3.6 12 12 3.3Z" /><path d="M8.1 12h7.8M13.5 9.6l2.4 2.4-2.4 2.4" /></svg>;
    case "today-sell":
      return <svg {...commonProps}><path d="M5 18.7h14" /><path d="m8 16 8.8-8.8M11.8 7.2h5v5" /><path d="M6 5.3h5.2M6 5.3v5.2" /></svg>;
    case "today-employment":
      return <svg {...commonProps}><rect x="3.8" y="7.1" width="16.4" height="11.4" rx="2" /><path d="M8.8 7.1V5.5c0-.8.7-1.5 1.5-1.5h3.4c.8 0 1.5.7 1.5 1.5v1.6M3.8 11.4c4.9 2.4 11.5 2.4 16.4 0M12 11.7v2.2" /></svg>;
    case "today-services":
      return <svg {...commonProps}><path d="m12 3 1.3 5.2L18 11l-4.7 2.8L12 19l-1.3-5.2L6 11l4.7-2.8L12 3Z" /><path d="m18.3 4.1.5 2 1.8 1.1-1.8 1.1-.5 2-.5-2L16 7.2l1.8-1.1.5-2Z" /></svg>;
    case "today-forum":
      return <svg {...commonProps}><path d="M4 5.2h12.3c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2H9l-4.6 3v-3.4A2 2 0 0 1 2 12.9V7.2c0-1.1.9-2 2-2Z" /><path d="M6.6 9h7.2M6.6 12h5" /></svg>;
    case "today-outings":
      return <svg {...commonProps}><circle cx="12" cy="12" r="4.2" /><ellipse cx="12" cy="12" rx="9.1" ry="5.2" transform="rotate(-28 12 12)" /><path d="m17.6 4.5.5 1.8 1.6.9-1.6 1-.5 1.8-.4-1.8-1.6-1 1.6-.9.4-1.8Z" /></svg>;
    case "today-animals":
      return <svg {...commonProps}><circle cx="7.2" cy="8.1" r="1.7" /><circle cx="11" cy="5.9" r="1.7" /><circle cx="15" cy="6.5" r="1.7" /><circle cx="18" cy="9.5" r="1.7" /><path d="M12.3 10.1c-2.8 0-5.7 2.5-5.7 5.1 0 1.8 1.4 3 3.1 3 .9 0 1.7-.4 2.6-.4.8 0 1.6.4 2.5.4 1.8 0 3.1-1.2 3.1-3 0-2.6-2.8-5.1-5.6-5.1Z" /></svg>;
    case "today-agenda":
      return <svg {...commonProps}><rect x="4" y="5.5" width="16" height="14" rx="2" /><path d="M8 3.8v3.4M16 3.8v3.4M4 9.2h16M8 12.5h2M13 12.5h3M8 15.8h2M13 15.8h3" /></svg>;
    case "today-infini":
      return <svg {...commonProps}><path d="M8.1 7.4c-2.8 0-5 2-5 4.6s2.2 4.6 5 4.6c3.7 0 5.6-9.2 9.8-9.2 2.8 0 5 2 5 4.6s-2.2 4.6-5 4.6C13.7 16.6 11.8 7.4 8.1 7.4Z" /><circle cx="12" cy="12" r="9.6" opacity=".25" /></svg>;
    case "vision-france-3024":
      return <svg {...commonProps}><path d="m12 2.9 8 4.6v9L12 21.1l-8-4.6v-9l8-4.6Z" /><path d="M9 16.4V7.6h6M9 11.7h4.5" /></svg>;
    case "vision-human-ai":
      return <svg {...commonProps} stroke="none" fill="currentColor"><text x="12" y="15.4" textAnchor="middle" fontSize="9.4" fontWeight="700" letterSpacing="-.6">44</text><path d="M12 2.8 20 7.4v9.2L12 21.2 4 16.6V7.4L12 2.8Zm0 1.6L5.4 8.2v7.6l6.6 3.8 6.6-3.8V8.2L12 4.4Z" opacity=".72" /></svg>;
    case "vision-lia-evolution":
      return <svg {...commonProps}><path d="m12 3.3 3 3-3 3-3-3 3-3ZM6.1 14.7l2.5 2.5-2.5 2.5-2.5-2.5 2.5-2.5ZM17.9 14.7l2.5 2.5-2.5 2.5-2.5-2.5 2.5-2.5Z" /><path d="M12 9.3v3.1c0 1.5-1.2 2.7-2.7 2.7H8M12 12.4c0 1.5 1.2 2.7 2.7 2.7H16" /></svg>;
    case "vision-et-civilizations":
      return <svg {...commonProps}><circle cx="12" cy="12" r="3.1" /><ellipse cx="12" cy="12" rx="9.1" ry="4.6" transform="rotate(-18 12 12)" /><path d="m18 4 .6 2.2 2 1.2-2 1.1-.6 2.2-.6-2.2-2-1.1 2-1.2L18 4Z" /></svg>;
    case "vision-eternal-health":
      return <svg {...commonProps}><circle cx="12" cy="12" r="8.8" /><path d="M12 6.7v10.6M6.7 12h10.6" /><path d="M7.8 7.8c3.5.6 5.6 3.4 4.2 9.5M16.2 7.8c-3.5.6-5.6 3.4-4.2 9.5" opacity=".65" /><circle cx="12" cy="12" r="1.2" /></svg>;
    case "vision-temples":
      return <svg {...commonProps}><path d="m12 3 8.4 4.1H3.6L12 3ZM5 19.5h14M6.5 9.2v7.6M10.2 9.2v7.6M13.8 9.2v7.6M17.5 9.2v7.6" /><path d="M4 7.1h16M4.8 16.8h14.4" /></svg>;
    case "vision-infrastructures":
      return <svg {...commonProps}><path d="m12 3 7.8 4.5v9L12 21l-7.8-4.5v-9L12 3Z" /><circle cx="12" cy="8" r="1.5" /><circle cx="8" cy="15" r="1.5" /><circle cx="16" cy="15" r="1.5" /><path d="m11.2 9.3-2.4 4.4M12.8 9.3l2.4 4.4M9.5 15h5" /></svg>;
    case "vision-dossiers":
      return <svg {...commonProps}><path d="M5 4.3h9.6c1.1 0 2 .9 2 2v13.4H7c-1.1 0-2-.9-2-2V4.3Z" /><path d="M7.4 7.4h6.8M7.4 10.5h6.8M7.4 13.6h4.8M16.6 7.1H19v12.6H9" /></svg>;
    default:
      return <EternuaIcon name={iconForPath(route)} />;
  }
}

export function FeatureCard({ feature, compact = false, presentation = "default", photo, photoAlt }) {
  useSourceLanguage();
  const { lang } = useI18n();
  const { translate, translateLabel, translateMessage } = useNavigationTranslations();
  if (!feature) return null;
  const title = translate(`navigation.catalog.${feature.featureKey}`, translateLabel(feature.title));
  const isBeta = ["BETA", "PRIVATE_BETA"].includes(feature.functionalStatus);
  const isTemple = presentation === "temple";
  const isVision = feature.section === "vision";
  const visual = imageForFeature(feature);
  const image = visual?.src || photo;
  const documentedDetails = (<>
      {isTemple ? (
        <div className="feature-card__status-block">
          <span>{translateLabel(isVision ? "Cadre éditorial" : "Capacités documentées")}</span>
          <p className="feature-card__status-copy">{<LocalizedText source={feature.statusExplanation} />}</p>
        </div>
      ) : <p className="feature-card__status-copy">{<LocalizedText source={feature.statusExplanation} />}</p>}
      {feature.simulatedCapabilities?.length ? (
        <div className="feature-card__limits">
          <strong>{translateLabel("Ce qui est simulé")}</strong>
          <ul>{feature.simulatedCapabilities.map((item) => <li key={item}>{<LocalizedText source={item} />}</li>)}</ul>
        </div>
      ) : null}
      {feature.limitations?.length ? (
        <div className="feature-card__limits">
          {isTemple ? (
            <div className="feature-card__limits-heading">
              <span aria-hidden="true">◇</span>
              <strong>{translateLabel("Limites connues")}</strong>
            </div>
          ) : <strong>{translateLabel("Limites connues")}</strong>}
          <ul>{feature.limitations.map((limit) => <li key={limit}>{<LocalizedText source={limit} />}</li>)}</ul>
        </div>
      ) : null}
  </>);
  return (
    <article className={`feature-card feature-card--${feature.section}${compact ? " feature-card--compact" : ""}${isTemple ? " feature-card--temple" : ""}${image ? " feature-card--photo" : ""}`} data-feature-key={feature.featureKey}>
      {image ? <div className="feature-card__photo"><img src={image} alt={displayAttribute(photoAlt || visual?.alt || "")} width={visual?.width || 1100} height={visual?.height || 732} loading="lazy" decoding="async" /></div> : null}
      {isTemple ? <span className="feature-card__aura" aria-hidden="true" /> : null}
      <div className="feature-card__top">
        <span className="feature-card__icon" aria-hidden="true">
          {isTemple ? <TempleFeatureIcon featureKey={feature.featureKey} route={feature.route} /> : (feature.icon || "∞")}
        </span>
        <FeatureBadges feature={feature} />
      </div>
      {isTemple ? <span className="feature-card__overline">{translateLabel(isVision ? "Contenu prospectif" : "Usage actuel")}</span> : null}
      <h3>{title}</h3>
      <p className="feature-card__description">{<LocalizedText source={feature.description} />}</p>
      {feature.publicNotice ? <p className="feature-card__notice"><strong>{<LocalizedText source={feature.publicNotice} />}</strong></p> : null}
      {compact && (isTemple || image) ? (
        <details className="feature-card__details">
          <summary>{translateLabel("Fonctionnement et limites")}</summary>
          <div className="feature-card__details-body">{documentedDetails}</div>
        </details>
      ) : documentedDetails}
      <div className="feature-card__footer">
        <FeaturePrimaryAction feature={feature} label={isTemple && isVision ? "Explorer la vision" : undefined} temple={isTemple} />
        {isBeta ? (
          <a className="feature-report-link" aria-label={translateMessage("navigation.feature.report", `Signaler un problème — ${feature.title}`, { title })} title={translateLabel("Signaler un problème")} href={`mailto:contact@eternua.com?subject=${encodeURIComponent(translateMessage("navigation.feature.report_subject", `Signalement bêta — ${feature.title}`, { title }))}`}>
            {translateLabel(compact && isTemple ? "Signaler" : "Signaler un problème")}{isTemple ? <span aria-hidden="true"> ↗</span> : null}
          </a>
        ) : null}
        {isTemple ? (
          <small className="feature-card__verification">
            <span aria-hidden="true">◷</span>
            <span>{translateLabel("Vérifié le")} <time dateTime={feature.lastVerifiedAt || undefined}>{translateLabel(formatRegistryDate(feature.lastVerifiedAt, lang))}</time></span>
          </small>
        ) : <small>{translateLabel("Testé le")} {translateLabel(formatRegistryDate(feature.lastTestedAt, lang))}</small>}
      </div>
    </article>
  );
}

export function formatRegistryDate(value, lang = "fr") {
  if (!value) return "date non renseignée";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long", year: "numeric" });
}
